import { Link } from "react-router-dom";
import ScriptTrackMockup from "./ScriptTrackMockup";
import TrustBadges from "./TrustBadges";
import type { LandingPageContent } from "@shared/landingPages";

/**
 * DscsaHero — above-the-fold hero section for the DSCSA landing page.
 * Two-column grid: copy + CTA on the left, ScriptTrack mockup on the right.
 * Follows the Hormozi conversion framework: outcome headline → effort-collapse
 * subheadline → primary CTA → trust micro-badges.
 */
export default function DscsaHero({
  onCtaClick,
  onSecondaryClick,
  content,
}: {
  onCtaClick?: () => void;
  onSecondaryClick?: () => void;
  content: LandingPageContent;
}) {
  return (
    <section className="lp-dscsa-hero relative overflow-hidden py-10 lg:py-16 landing-grid">
      <div className="absolute right-[-8rem] top-[-8rem] h-80 w-80 rounded-full border-[48px] border-accent/20" aria-hidden="true" />
      <div className="absolute left-1/2 bottom-0 h-px w-1/2 bg-accent/50" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left column: copy + CTA ──────────────────────────────── */}
          <div className="flex flex-col gap-6 landing-reveal lp-hero-copy">
            {/* The managed offer is the primary promise; the deadline is useful
                context but is not presented as an approval countdown. */}
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit lp-hero-kicker">
              <span className="w-2 h-2 rounded-full bg-accent inline-block" aria-hidden="true" />
              DSCSA / a practical starting point
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[4.2rem] font-bold leading-[.98] tracking-[-.045em] max-w-xl">
              {content.headline}
            </h1>

            {/* Subheadline — collapses perceived effort */}
            <p className="text-white/75 text-lg leading-relaxed max-w-lg lp-hero-description">
              {content.subheadline}
            </p>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <Link
                to={content.ctaDestination}
                onClick={onCtaClick}
                 className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-600 active:bg-accent-600 text-white font-bold text-base px-8 py-4 rounded-btn shadow-lg shadow-black/20 transition-colors duration-200 w-full sm:w-auto lp-hero-cta"
              >
                {content.ctaText}
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a
                href="#readiness-assessment"
                 onClick={onSecondaryClick}
                className="inline-flex items-center justify-center text-white/85 hover:text-white font-semibold text-sm px-2 py-3 underline underline-offset-4 transition-colors lp-hero-secondary"
              >
                Not ready to register? Check your readiness
              </a>
            </div>

            {/* Trust micro-badges */}
            <TrustBadges variant="light" className="justify-start" signals={content.trustSignals} />

            {/* Secondary reassurance line */}
            <p className="text-white/50 text-xs lp-hero-reassurance">
              {content.reassurance}
            </p>
          </div>

          {/* ── Right column: ScriptTrack mockup ─────────────────────── */}
          <div className="flex justify-center lg:justify-end landing-reveal-delay">
            <ScriptTrackMockup variant="compliance" />
          </div>

        </div>
      </div>
    </section>
  );
}
